import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { IconLibrary } from "@@/shared/icon-library";
import type { IconName } from "@@/shared/icon-name";
import { statusIdZodSchema } from "@@/shared/statuses/status-id";
import { statusReferencesZodObject } from "@@/shared/statuses/status-references";
import type { TranslatableValue } from "@@/shared/translations/translation";
import { translation } from "@@/shared/translations/translation";
import { customerIdZodSchema } from "@@/shared/users/customer-id";
import { systemUserZodSchema } from "@@/shared/users/system-user";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";

const reservationCheckInStatusValues = [
    "CHECKED_OUT",
    "CHECKED_IN",
    "PENDING",
] as const;
const reservationCheckInStatusValueZodEnum = z.enum(
    reservationCheckInStatusValues,
);
type ReservationCheckInStatusValue =
    (typeof reservationCheckInStatusValues)[number];

const reservationCheckInStatusMeta: Record<
    ReservationCheckInStatusValue,
    {
        iconName: IconName;
        iconLibrary: IconLibrary;
        title: TranslatableValue;
    }
> = {
    CHECKED_OUT: {
        iconName: "faCircleCheck",
        iconLibrary: "regular",
        title: translation({
            sv: "Utcheckad",
            en: "Checked out",
        }),
    },
    CHECKED_IN: {
        iconName: "faCircleCheck",
        iconLibrary: "solid",
        title: translation({
            sv: "Incheckad",
            en: "Checked in",
        }),
    },
    PENDING: {
        iconName: "faBan",
        iconLibrary: "regular",
        title: translation({
            sv: "Väntar på incheckning",
            en: "Pending check in",
        }),
    },
};

const reservationCheckInStatusZodObject = z.object({
    _id: statusIdZodSchema,
    _type: z.literal("RESERVATION_CHECK_IN_STATUS"),
    _createdAt: isoAndUnixZodSchema,
    setAt: isoAndUnixZodSchema,
    references: statusReferencesZodObject,
    checkInEnabled: z.boolean(),
    status: reservationCheckInStatusValueZodEnum,
    setBy: z.union([userIdZodSchema, customerIdZodSchema, systemUserZodSchema]),
    message: z.string().optional(),
});
type ReservationCheckInStatus = z.infer<
    typeof reservationCheckInStatusZodObject
>;

type FactoryParams = Except<ReservationCheckInStatus, "_createdAt" | "_type">;
const reservationCheckInStatusFactory = (
    params: FactoryParams,
): ReservationCheckInStatus => {
    return {
        ...params,
        _type: "RESERVATION_CHECK_IN_STATUS",
        _createdAt: nowIsoAndUnix(),
    };
};

export {
    reservationCheckInStatusFactory,
    reservationCheckInStatusMeta,
    reservationCheckInStatusValues,
    reservationCheckInStatusValueZodEnum,
    reservationCheckInStatusZodObject,
};
export type { ReservationCheckInStatus, ReservationCheckInStatusValue };
